<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminAlertGroupNameList"
        label="Alert Group Names"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Alert Group Names</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} Group Names</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search group name">
            <b-input
              v-model="search"
              placeholder="Search by group name..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="groupNames"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="groupName"
          default-sort-direction="asc"
        >
          <b-table-column field="groupName" label="Group Name" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.group_name }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No group names found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createGroupNames">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create new group name</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field
                label="Group Name"
                required
                :type="{ 'is-danger': !!createErrors.group_name }"
                :message="createErrors.group_name"
              >
                <b-input
                  v-model="createForm.group_name"
                  placeholder="Enter group name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdateGroupName">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit group name</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Group Name" :type="{ 'is-danger': !!errors.group_name }" :message="errors.group_name">
                <b-input
                  v-model="editForm.group_name"
                  placeholder="Enter group name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
export default {
  name: 'AdminAlertGroupNameList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      groupNames: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        group_name: '',
      },
      createErrors: {},
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingRow: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadGroupNames();
  },
  methods: {
    async loadGroupNames() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('term', this.search.trim());
        const response = await request.get(`/admin/alerts/group-names?${params.toString()}`);
        this.groupNames = response.data.group_names || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading group names:', error);
        this.$buefy.toast.open({
          message: 'Failed to load group names',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadGroupNames();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadGroupNames();
      }, 300);
    },
    openCreateModal() {
      this.createForm = {
        group_name: '',
      };
      this.createErrors = {};
      this.isCreateModalActive = true;
    },
    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        group_name: '',
      };
      this.createErrors = {};
    },
    async createGroupNames() {
      this.isCreating = true;
      this.createErrors = {};
      try {
        const payload = {
          group_name: this.createForm.group_name?.trim(),
        };
        await request.post('/admin/alerts/group-names', payload);
        this.$buefy.toast.open({
          message: 'Group name created successfully',
          type: 'is-success',
        });
        this.closeCreateModal();
        this.loadGroupNames();
      } catch (error) {
        console.error('Error creating group name:', error);
        if (error.response?.data?.errors) {
          this.createErrors = error.response.data.errors;
        } else {
          const msg = error.response?.data?.code == 422 ? 'Group name already exists' : 'Failed to create group name';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        }
      } finally {
        this.isCreating = false;
      }
    },
    openEditModal(obj) {
      this.editingRow = obj;
      this.editForm = {
        group_name: obj.group_name,
      };
      this.errors = {};
      this.showEditModal = true;
    },
    async handleUpdateGroupName() {
      this.editLoading = true;
      this.errors = {};
      try {
        const payload = {
          group_name: this.editForm.group_name?.trim(),
        };
        await request.put(`/admin/alerts/group-names/${this.editingRow.id}`, payload);
        this.$buefy.toast.open({
          message: 'Group name updated successfully',
          type: 'is-success',
        });
        this.showEditModal = false;
        this.loadGroupNames();
      } catch (error) {
        console.error('Error updating group name:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          const msg = error.response?.data?.code == 422 ? 'Group name already exists' : 'Failed to update group name';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update group name',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },
    confirmDelete(obj) {
      this.$buefy.dialog.confirm({
        title: 'Delete group name',
        message: `Are you sure you want to delete "<strong>${obj.group_name}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteGroupName(obj),
      });
    },
    async deleteGroupName(obj) {
      try {
        await request.delete(`/admin/alerts/group-names/${obj.id}`);
        this.$buefy.toast.open({
          message: 'Group name deleted successfully',
          type: 'is-success',
        });
        this.loadGroupNames();
      } catch (error) {
        console.error('Error deleting group name:', error);
        let errorMessage = 'Failed to delete group name';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this group name — it may still be in use by alerts';
        }
        this.$buefy.dialog.alert({
          title: 'Delete failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
